import React, { useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { func, bool } from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import {
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
  FormHelperText,
  styled,
} from '@nextiva/next-ui-components';
import { isValidPhoneNumber } from 'libphonenumber-js';
import {
  setBusyGreetingOptions,
  setStorageType,
  setTransferOnZero,
} from 'containers/VoiceMail/reducers/advanced';
import {
  setAdvancedFormRef,
  setAdvancedFormReset,
} from 'containers/VoiceMail/reducers/formsState';
import * as regex from 'constants/regex';
import { pxToRem } from 'utils/pxToRemConverter';
import Hint from 'components/VoicemailHint';
import sassVars from 'styles/application/_base/_variables.scss';

const EmailSection = styled('section')`
  display: flex;
  align-items: center;
  height: ${({ storageType }) => (storageType === 'SEND_EMAIL' ? '52px' : '0')};
  margin: 0 0 0 43px;
  overflow: hidden;
  transition: all 0.5s;
`;

const EmailAddress = styled('div')`
  margin: 0 16px 0 0;
  font-size: ${pxToRem('14px')};
  font-weight: 500;
  letter-spacing: 0.21px;
`;

const FormControlStyled = styled(FormControl)`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 32px;

  .MuiFormControlLabel-root {
    margin-left: 0;
  }
`;

const FormLabelStyled = styled(FormLabel)`
  &.MuiFormLabel-root {
    margin: 0 0 8px;
  }
`;

const SelectOptions = styled(MenuItem)`
  &.MuiMenuItem-root {
    margin-bottom: 2px;
    color: ${sassVars.menuLinkActiveColor};
    font-family: Lato, sans-serif;
    font-size: ${pxToRem('16px')};
    font-weight: 400;
    line-height: 19px;
    &:hover {
      background-color: ${sassVars.dividerColor};
    }
  }
`;

const Busy = styled(Select)`
  width: 100%;
  margin: 0 24px 0 0;
  & .MuiOutlinedInput-input {
    padding: 10.5px 32px 10.5px 14px;
    color: ${sassVars.menuLinkActiveColor};
    font-family: Lato, sans-serif;
    font-size: ${pxToRem('16px')};
    font-weight: 500;
  }
  & .MuiOutlinedInput-notchedOutline {
    border-color: ${sassVars.selectBorderColor};
  }
  &.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${sassVars.selectBorderColor};
  }
  &.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${sassVars.selectBorderColor};
    border-width: 1px;
  }
  & .MuiSelect-icon {
    color: ${sassVars.selectBorderColor};
  }
`;

const TextField = styled(OutlinedInput)`
  font-family: Lato, sans-serif;
  font-weight: 400;
  margin: 0 24px 0 0;
  & .MuiOutlinedInput-input {
    padding: 10.5px 14px 10.5px 14px;
  }
  &.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${sassVars.selectBorderColor};
  }
  &.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${sassVars.primaryBlue};
    border-width: 1px;
  }
  & .MuiOutlinedInput-notchedOutline {
    border-color: ${sassVars.selectBorderColor};
  }
`;

const iconBtn = `
  &.MuiButtonBase-root {
    padding: 9px;
  }
  &.MuiIconButton-colorSecondary:hover {
    background-color: ${sassVars.secondaryColorHover};
  }
  &.MuiTypography-body1 {
    font-size: ${pxToRem('14px')};
  }
`;

const Label = styled(FormControlLabel)`
  margin-top: 9px;
  .MuiTypography-body1 {
    font-family: Lato, sans-serif;
    font-size: ${pxToRem('14px')};
    line-height: 20px;
    font-weight: 500;
    letter-spacing: 0.21px;
    margin-right: 16px;
  }
`;

const CheckboxStyled = styled(Checkbox)`
  &.MuiCheckbox-root {
    color: ${sassVars.secondaryColor};
  }
  &.MuiCheckbox-root.Mui-checked {
    color: ${sassVars.primaryBlue};
  }
  &.MuiCheckbox-colorSecondary.Mui-checked:hover {
    background-color: ${sassVars.secondaryColorHover};
  }
  ${iconBtn}
`;

const RadioBtn = styled(Radio)`
  flex-direction: column;
  &.MuiRadio-colorSecondary.Mui-checked {
    color: ${sassVars.primaryBlue};
  }
  ${iconBtn}
`;

const HelperText = styled(FormHelperText)`
  position: absolute;
  bottom: -20px;
  height: 19px;
  color: ${sassVars.error};
  font-family: Lato, sans-serif;
  font-weight: 300;
`;

const AdvancedForm = ({
  setAdvancedIsDirty = () => {},
  setAdvancedHasErrors = () => {},
  onSubmit = () => {},
  isVoicemailActive = false,
  isHipaaEnabled = false,
  'data-testid': dataTestId,
}) => {
  const {
    control,
    setValue,
    handleSubmit,
    formState,
    formState: { errors },
    reset,
    clearErrors,
    getValues,
  } = useForm({
    defaultValues: {
      currentBusyPersonalAudioFile: '',
      transferNumber: '',
      transferOn0: false,
      Allcalls: false,
      busyCalls: false,
      unansweredCalls: false,
    },
  });

  const dispatch = useDispatch();
  const busyGreetingOptions = useSelector(
    (state) => state.advanced.busyGreetingOptions,
  );
  const storageType = useSelector((state) => state.advanced.storageType);
  const response = useSelector((state) => state.advanced.response);
  const transferOnZero = useSelector((state) => state.advanced.transferOnZero);

  const advancedFormRef = useRef(null);

  useEffect(() => {
    dispatch(setAdvancedFormReset(reset));
    dispatch(setAdvancedFormRef(advancedFormRef));
  }, []);

  useEffect(() => {
    setAdvancedIsDirty(formState.isDirty);
    const hasErrors = Object.keys(formState.errors).length > 0;
    setAdvancedHasErrors(hasErrors);
  }, [formState]);

  useEffect(() => {
    if (!response) return;
    // set the busy greeting options and current value
    setValue(
      'currentBusyPersonalAudioFile',
      response.currentBusyPersonalAudioFile,
    );

    if (!busyGreetingOptions.length)
      dispatch(setBusyGreetingOptions(response.availableAnnouncements));
    // calls
    setValue('allCalls', response.allCalls);
    setValue('busyCalls', response.busyCalls);
    setValue('unansweredCalls', response.unansweredCalls);
    // transfer 0
    setValue('transferOn0', response.transferOn0);
    dispatch(setTransferOnZero(response.transferOn0));
    setValue('transferNumber', response.transferNumber || '');
    // voicemail storage
    if (isHipaaEnabled) {
      setValue('storeOnNextivaSystem', true, { shouldDirty: false });
      dispatch(setStorageType('NEXTIVA_STORE'));
    } else {
      setValue('storeOnNextivaSystem', response.storeOnNextivaSystem);
      if (!response.storeOnNextivaSystem)
        dispatch(setStorageType('SEND_EMAIL'));
    }
    setValue('personalEmail', response.personalEmail || '');
  }, [response]);

  const onStorageChange = (e) => {
    dispatch(setStorageType(e.target.value));
    if (e.target.value === 'NEXTIVA_STORE')
      setValue('storeOnNextivaSystem', true, { shouldDirty: true });
    if (e.target.value === 'SEND_EMAIL')
      setValue('storeOnNextivaSystem', false, { shouldDirty: true });
  };

  const onTransferNumberClick = (e) => {
    dispatch(setTransferOnZero(e.target.checked));
    clearErrors('transferNumber');
  };

  const onNextivaStoreClick = () => {
    if (
      errors.personalEmail?.type === 'pattern' ||
      errors.personalEmail?.type === 'required'
    ) {
      setValue('personalEmail', '');
      clearErrors('personalEmail');
    }
  };

  const validateInternationalPhone = (transferNumber) => {
    if (transferNumber[0] === '+') {
      const isValidInternationalPhoneNumber = isValidPhoneNumber(
        transferNumber,
      );
      return isValidInternationalPhoneNumber;
    }
    return true;
  };

  const validatePhoneAndExtension = (transferNumber) => {
    if (transferNumber[0] !== '+' && transferNumber.length > 0) {
      return regex.phoneNumUsa.test(transferNumber);
    }
    return true;
  };

  const validateMinLength = (transferNumber) => {
    if (transferNumber.length > 0) {
      const numberClear = transferNumber.replaceAll(/[ -]/g, '');
      return numberClear.length >= 2;
    }
    return true;
  };
  const setValueOnBlur = (input) => {
    const inputValue = getValues(input);

    if (!inputValue) {
      setValue(input.toString(), '');
    }
  };

  return (
    <form
      ref={advancedFormRef}
      onSubmit={handleSubmit(onSubmit)}
      data-testid={`${dataTestId}-Form`}
    >
      <FormLabelStyled
        component="legend"
        data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-BusyGreetingLabel"
      >
        <Hint
          tool="BUSY GREETING"
          tip='You can add a busy greeting by uploading an audio file in the greetings section. The default busy greeting is "The party you are trying to reach(or your recorded name), is busy and cannot take your call, please leave a message after the tone...".'
        />
      </FormLabelStyled>
      <FormControlStyled component="fieldset" variant="outlined">
        <Controller
          name="currentBusyPersonalAudioFile"
          control={control}
          defaultValue=""
          render={({ field }) => {
            return (
              <Busy
                MenuProps={{
                  getContentAnchorEl: null,
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'center',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'center',
                  },
                }}
                {...field}
                onBlur={() => setValueOnBlur('currentBusyPersonalAudioFile')}
                disabled={!isVoicemailActive}
                data-testid={dataTestId}
              >
                {busyGreetingOptions.map((option) => {
                  return (
                    <SelectOptions
                      key={option.greetingName}
                      value={option.greetingName}
                      data-testid={`PlatformAdminUI-TeamVoiceSettings-VoiceMail-BusyGreetingMenuList-${option.greetingName}`}
                    >
                      {option.greetingName}
                    </SelectOptions>
                  );
                })}
              </Busy>
            );
          }}
        />
      </FormControlStyled>
      <FormControlStyled component="fieldset">
        <FormLabelStyled
          component="legend"
          data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-CallTypesLabel"
        >
          <Hint tool="SELECT WHAT TYPES OF CALLS CAN GO TO VOICEMAIL" tip="" />
        </FormLabelStyled>
        <Controller
          name="allCalls"
          control={control}
          defaultValue={false}
          render={({ field: { name, value, onChange, onBlur, ref } }) => {
            return (
              <Label
                control={
                  <CheckboxStyled
                    name={name}
                    ref={ref}
                    checked={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    disableRipple
                    disabled={!isVoicemailActive}
                    data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-AllCallsCheckBox"
                  />
                }
                label="All calls"
                data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-AllCallsLabel"
              />
            );
          }}
        />
        <Controller
          name="busyCalls"
          control={control}
          defaultValue={false}
          render={({ field: { name, value, onChange, onBlur, ref } }) => {
            return (
              <Label
                control={
                  <CheckboxStyled
                    name={name}
                    ref={ref}
                    checked={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    disableRipple
                    disabled={!isVoicemailActive}
                    data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-BusyCallsCheckBox"
                  />
                }
                label="Busy calls"
                data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-BusyCallsLabel"
              />
            );
          }}
        />
        <Controller
          name="unansweredCalls"
          control={control}
          defaultValue={false}
          render={({ field: { name, value, onChange, onBlur, ref } }) => {
            return (
              <Label
                control={
                  <CheckboxStyled
                    name={name}
                    ref={ref}
                    checked={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    disableRipple
                    disabled={!isVoicemailActive}
                    data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-UnansweredCallsCheckBox"
                  />
                }
                label="Unanswered calls"
                data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-UnansweredCallsLabel"
              />
            );
          }}
        />
      </FormControlStyled>
      <FormControlStyled component="fieldset">
        <FormLabelStyled
          component="legend"
          data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-DuringVoiceMailGreetingLabel"
        >
          <Hint tool="DURING VOICEMAIL GREETING" tip="" />
        </FormLabelStyled>
        <Controller
          name="transferOn0"
          control={control}
          defaultValue={false}
          render={({ field: { name, value, onChange, onBlur, ref } }) => {
            return (
              <Label
                control={
                  <CheckboxStyled
                    name={name}
                    ref={ref}
                    checked={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    onClick={onTransferNumberClick}
                    disableRipple
                    disabled={!isVoicemailActive}
                    data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-TransferNumberCheckBox"
                  />
                }
                label='Transfer on "0" to this number'
                data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-TransferNumberLabel"
              />
            );
          }}
        />
        <div>
          <Controller
            name="transferNumber"
            control={control}
            defaultValue=""
            rules={{
              required: transferOnZero,
              validate: {
                minLength: validateMinLength,
                international: validateInternationalPhone,
                numberOrExtension: validatePhoneAndExtension,
              },
            }}
            render={({ field }) => {
              return (
                <TextField
                  placeholder="Phone number"
                  {...field}
                  error={
                    errors.transferNumber?.type === 'required' ||
                    errors.transferNumber?.type === 'minLength' ||
                    errors.transferNumber?.type === 'international' ||
                    errors.transferNumber?.type === 'numberOrExtension'
                  }
                  disabled={!isVoicemailActive}
                  onBlur={() => setValueOnBlur('transferNumber')}
                  data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-TransferPhoneNumber"
                />
              );
            }}
          />
          <HelperText>
            {errors.transferNumber?.type === 'required' &&
              'When you selected to transfer on 0 a phone number is required..'}
          </HelperText>
          <HelperText>
            {errors.transferNumber?.type === 'minLength' &&
              'Must have a minimum of 2 digits.'}
          </HelperText>
          <HelperText>
            {errors.transferNumber?.type === 'international' &&
              'Invalid phone number. An E.164 number used for outgoing calls needs to be 3 to 22 characters in length. Dashes are not included in this length. There must be at least one digit after the country code'}
          </HelperText>
          <HelperText>
            {errors.transferNumber?.type === 'numberOrExtension' &&
              'Phone number may only contain +, -, and 0-9. Only the leading character may be a + or *.'}
          </HelperText>
        </div>
      </FormControlStyled>
      {!isHipaaEnabled && (
        <FormControlStyled component="fieldset">
          <FormLabelStyled
            component="legend"
            data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-VoiceMailStorageOptionsLabel"
          >
            <Hint tool="VOICEMAIL STORAGE OPTIONS" tip="" />
          </FormLabelStyled>

          <RadioGroup
            aria-label="distribution"
            value={storageType}
            onChange={onStorageChange}
            data-testid={`${dataTestId}-RadioGroup`}
          >
            <Label
              value="NEXTIVA_STORE"
              control={
                <RadioBtn
                  disableRipple
                  disabled={!isVoicemailActive}
                  onClick={onNextivaStoreClick}
                  data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-NextivaStorageRadioButton"
                />
              }
              label="Store on Nextiva System"
              data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-NextivaStorageLabel"
            />
            <Label
              value="SEND_EMAIL"
              control={
                <RadioBtn
                  disableRipple
                  disabled={!isVoicemailActive}
                  data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-DoNotStoreRadioButton"
                />
              }
              label="Do not store, send email"
              data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-DoNotStoreLabel"
            />
            <EmailSection storageType={storageType}>
              <EmailAddress data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-EmailAddressLabel">
                E-mail address
              </EmailAddress>
              <div>
                <Controller
                  name="personalEmail"
                  control={control}
                  defaultValue=""
                  rules={{
                    pattern: regex.email,
                    required: !!(
                      !isHipaaEnabled && storageType === 'SEND_EMAIL'
                    ),
                  }}
                  render={({ field }) => {
                    return (
                      <TextField
                        {...field}
                        error={
                          errors.personalEmail?.type === 'pattern' ||
                          errors.personalEmail?.type === 'required'
                        }
                        disabled={!isVoicemailActive}
                        data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-EmailAddress"
                      />
                    );
                  }}
                />
                <HelperText>
                  {errors.personalEmail?.type === 'pattern' &&
                    'Enter a valid email.'}
                </HelperText>
                <HelperText>
                  {errors.personalEmail?.type === 'required' &&
                    'Requires valid email address.'}
                </HelperText>
              </div>
            </EmailSection>
          </RadioGroup>
        </FormControlStyled>
      )}
    </form>
  );
};

AdvancedForm.propTypes = {
  isVoicemailActive: bool.isRequired,
  setAdvancedHasErrors: func.isRequired,
  setAdvancedIsDirty: func.isRequired,
  onSubmit: func.isRequired,
  isHipaaEnabled: bool,
};

export default AdvancedForm;
